<template>
  <Flex column class="column-container">
    <Flex justify-center mb-3>{{ $t('updating.recordcount', [recordIds.length]) }}</Flex>
    <Flex v-for="(field, index) in fields" :key="index" class="row-container">
      <div class="input-container" :class="classObject(field)">
        <div v-if="isComponent(field.baseTypeName)">
          <FormField :label="field.label" :ref="field.fieldId">
            <ComponentLookupSelect
              v-model="field.value"
              :disabled="field.clear"
              :component="field.baseTypeName"
              v-bind:limit-width="true"
            />
          </FormField>
        </div>
        <div v-else>
          <FormField
            v-model="field.value"
            :disabled="field.clear"
            :label="field.label"
            :key="index"
            :type="getWizerType(field.baseTypeName)"
            :items="field.options"
            :ref="field.fieldId"
          >
          </FormField>
        </div>
      </div>
      <Checkbox
        v-if="showClear(field.baseTypeName)"
        :value.sync="field.clear"
        :label="$t('Button.Clear')"
        type="boolean"
        @update:value="clear(field, $event)"
      >
      </Checkbox>
    </Flex>
    <Flex class="row-container">
      <Checkbox
        v-if="this.showAllowDisableActions"
        :value.sync="runActions"
        :checked="runActions"
        :label="$t('run.actions')"
      ></Checkbox>
    </Flex>
    <Flex class="row-container">
      <Checkbox
        v-if="this.showAllowDisableValidations"
        :value.sync="runValidations"
        :checked="runValidations"
        :label="$t('run.validations')"
      ></Checkbox>
    </Flex>
    <Flex justify-center pb-48 class="button-container">
      <Button v-if="!inIframe" @click="update" color="primary" :disabled="disableBulkUpdate">
        Update
      </Button>
    </Flex>
  </Flex>
</template>

<script>
import { mapState } from 'vuex'
import Flex from '@wizer/core/components/flex/Flex.vue'
import Button from '@wizer/core/components/button/Button.vue'
import FormField from '@wizer/core/components/form/FormField'
import Checkbox from '@wizer/core/components/checkbox/Checkbox.vue'
import ComponentLookupSelect from '../ComponentLookupSelect'
import BulkUpdateService from '../../services/BulkUpdateService'
import IframeMixin from '../../mixins/IframeMixin'
import E360BaseTypes from '../../constants/E360BaseTypes'

const e360ToWizerTypeMapping = {
  [E360BaseTypes.DATE]: 'date',
  [E360BaseTypes.LOOKUP]: 'choice',
  [E360BaseTypes.QUESTION_YN]: 'choice',
  [E360BaseTypes.TEXT]: 'string',
}

export default {
  name: 'BulkUpdateInput',
  mixins: [IframeMixin],

  components: {
    Flex,
    Button,
    FormField,
    ComponentLookupSelect,
    Checkbox,
  },

  created() {
    this.$store.dispatch('fetchAllFieldDetails', {
      fieldIds: this.fieldIds,
      rootType: this.rootType,
      bpdId: this.bpdId,
      listBpdId: this.listBpdId,
    })
  },

  mounted() {
    this.registerProcessCallback(this.update)
    this.fieldsModifiedWatcher = this.$watch(
      'fields',
      (old, newval) => {
        this.toggleSave(old, newval)
      },
      { deep: true },
    )
  },

  props: ['rootType', 'fieldIds', 'recordIds', 'bpdId', 'listBpdId'],

  data() {
    return {
      disableBulkUpdate: true,
      runValidations: true,
      runActions: true,
      fieldsModifiedWatcher: null,
    }
  },

  computed: {
    ...mapState({
      fields: (state) => state.BulkUpdateFields.fields,
      allowDisableValidations: (state) => state.BulkUpdateFields.controlValidationsField,
      allowDisableActions: (state) => state.BulkUpdateFields.controlActionsField,
    }),
    showAllowDisableValidations() {
      return typeof this.allowDisableValidations !== 'function' && this.allowDisableValidations
    },
    showAllowDisableActions() {
      return typeof this.allowDisableActions !== 'function' && this.allowDisableActions
    },
  },

  methods: {
    async update() {
      const self = this
      self.fieldsModifiedWatcher()
      const nonEmptyValues = self.getNonEmptyValues(self.fields)
      const content = window.parent
      if (nonEmptyValues.length > 0) {
        const data = {
          componentId: this.rootType,
          recordIds: this.recordIds.join(','),
          values: nonEmptyValues,
          executeValidations: this.runValidations,
          executeActions: this.runActions,
          bpdId: this.bpdId,
        }
        let bulkUpdateResponse
        const feedbackObject = {}
        try {
          bulkUpdateResponse = await BulkUpdateService.postBulkUpdate(data)
          feedbackObject.rootType = this.rootType
          feedbackObject.bulkUpdateResponse = bulkUpdateResponse.data
        } catch (error) {
          const message = error.response ? error.response.data : error
          feedbackObject.error = message
          const notification = { type: 'error', message }
          this.$store.dispatch('Notification/add', notification, { root: true })
        } finally {
          this.$store.dispatch('setFeedbackData', feedbackObject)
          content.postMessage(
            {
              status: 'vue-loaded',
              isShowingFeedback: 'true',
            },
            window.location.origin,
          )
        }
      } else {
        content.postMessage(
          {
            status: 'vue-success',
          },
          window.location.origin,
        )
      }
    },
    classObject(field) {
      return {
        short: [E360BaseTypes.LOOKUP, E360BaseTypes.DATE, E360BaseTypes.QUESTION_YN].some((v) =>
          field.baseTypeName.includes(v),
        ),
      }
    },
    clear(field, checked) {
      if (checked) {
        field.cleared = true
        const inputElement = this.$refs[field.fieldId][0].inputElement
        if (inputElement) {
          if (field.baseTypeName === E360BaseTypes.DATE) {
            inputElement.inputSetValue(null)
          } else {
            inputElement.inputSetValue('')
          }
        }
      } else if (field) {
        field.cleared = false
      }
    },
    toggleSave(old, newVal) {
      if (newVal[0]) {
        const modifiedFields = this.fields.filter((field) => {
          return this.getFieldValue(field.value) || field.clear
        })
        if (modifiedFields.length > 0 === this.disableBulkUpdate) {
          this.disableBulkUpdate = !this.disableBulkUpdate
          if (this.inIframe) {
            const content = window.parent
            content.postMessage({ status: 'vue-execute' }, window.location.origin)
          }
        }
      }
    },
    getNonEmptyValues(fields) {
      const nonEmptyValues = []
      fields.forEach((field) => {
        if (field.cleared || field.value) {
          const nonEmptyValue = {
            fieldId: field.fieldId,
            fieldValue: this.getFieldValue(field.value),
          }
          nonEmptyValues.push(nonEmptyValue)
        }
      })
      return nonEmptyValues
    },
    getFieldValue(value) {
      if (value instanceof Date) {
        /**
         * the server expects all dates to be submitted in the users timezone
         * if we store it as a date here it will get converted to the UTC timezone
         * when AXIOS makes the REST call to make the bulk update of data
         * and converts the date to ISO String when it builds the JSON to submit
         * thereby changing the date value on save the user had selected
         */
        const offset = value.getTimezoneOffset()
        return new Date(value.getTime() - offset * 60 * 1000).toISOString().split('T')[0]
      }
      if (value instanceof Object) {
        return value.target ? value.target.value : null
      }
      return value
    },
    showClear(baseTypeName) {
      return baseTypeName !== E360BaseTypes.QUESTION_YN
    },
    getWizerType(baseTypeName) {
      const matchedE360Type = Object.keys(e360ToWizerTypeMapping).find((e360TypeName) => {
        return baseTypeName.includes(e360TypeName)
      })
      return e360ToWizerTypeMapping[matchedE360Type]
    },
    isComponent(baseTypeName) {
      return baseTypeName.includes(E360BaseTypes.COMPONENT)
    },
  },
}
</script>

<style lang="postcss">
.row-container {
  justify-content: space-between;
}
.column-container {
  @apply .w-2/5;
}
.input-container {
  @apply .w-full;
}
.input-container.short {
  @apply .w-2/5;
}
.button {
  @apply .w-24;
}
</style>
